import { forwardRef, useEffect, useRef, useState, type ReactNode } from "react";
import AtmosphericBackdrop from "@/components/AtmosphericBackdrop";
import { cn } from "@/lib/utils";

type GlowSide = "left" | "right" | "center" | "none";
type Tone = "cream" | "warm" | "muted" | "dark";

interface SectionAtmosphereProps {
  src?: string;
  /** Public-path base for AVIF/WebP variants generated by optimize-images.mjs. */
  optimizedBase?: string;
  lqip?: string;
  /** Where the atmospheric image is anchored. */
  origin?: "top left" | "top right" | "bottom left" | "bottom right" | "center";
  /** Maximum opacity the image fades to as the section enters viewport. */
  intensity?: number;
  /** Side of the accent glow blob. */
  glow?: GlowSide;
  /** Background tone token. */
  tone?: Tone;
  className?: string;
  innerClassName?: string;
  id?: string;
  ariaLabelledBy?: string;
  children: ReactNode;
}

const toneToBg: Record<Tone, string> = {
  cream: "bg-background",
  warm: "bg-secondary",
  muted: "bg-muted/30",
  dark: "bg-primary text-primary-foreground",
};

const originToPosition: Record<NonNullable<SectionAtmosphereProps["origin"]>, string> = {
  "top left": "-top-24 -left-24 [mask-image:radial-gradient(ellipse_at_top_left,black_25%,transparent_75%)]",
  "top right": "-top-24 -right-24 [mask-image:radial-gradient(ellipse_at_top_right,black_25%,transparent_75%)]",
  "bottom left": "-bottom-24 -left-24 [mask-image:radial-gradient(ellipse_at_bottom_left,black_25%,transparent_75%)]",
  "bottom right": "-bottom-24 -right-24 [mask-image:radial-gradient(ellipse_at_bottom_right,black_25%,transparent_75%)]",
  center: "inset-0 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]",
};

const glowSideClass = (side: GlowSide) => {
  if (side === "none") return null;
  const pos =
    side === "left"
      ? "-left-40 top-1/3"
      : side === "right"
      ? "-right-40 top-1/3"
      : "left-1/2 -translate-x-1/2 top-1/3";
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute w-[520px] h-[520px] rounded-full bg-primary/[0.05] blur-[140px]",
        pos,
      )}
    />
  );
};

/**
 * Editorial section wrapper. Provides:
 *  - tone-aware background
 *  - optional atmospheric backdrop that crossfades on enter (0 → intensity)
 *  - optional accent glow blob anchored left/right/center
 *  - generous vertical rhythm
 *
 * Intentionally decorative — pass `aria-labelledby` if the inner heading has
 * an id so screen readers announce the section properly.
 */
const SectionAtmosphere = forwardRef<HTMLElement, SectionAtmosphereProps>(
  (
    {
      src,
      optimizedBase,
      lqip,
      origin = "top right",
      intensity = 0.12,
      glow = "right",
      tone = "cream",
      className,
      innerClassName,
      id,
      ariaLabelledBy,
      children,
    },
    ref,
  ) => {
    const localRef = useRef<HTMLElement | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
      const el = localRef.current;
      if (!el) return;
      const reduced =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) {
        setVisible(true);
        return;
      }
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) if (entry.isIntersecting) setVisible(true);
        },
        { rootMargin: "-10% 0px -10% 0px", threshold: 0.05 },
      );
      io.observe(el);
      return () => io.disconnect();
    }, []);

    return (
      <section
        ref={(el) => {
          localRef.current = el;
          if (typeof ref === "function") ref(el);
          else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el;
        }}
        id={id}
        aria-labelledby={ariaLabelledBy}
        className={cn(
          "relative overflow-hidden",
          toneToBg[tone],
          className,
        )}
      >
        {src && (
          <div
            aria-hidden="true"
            className="absolute inset-0 pointer-events-none"
            style={{
              opacity: visible ? intensity : 0,
              transition: "opacity 1200ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <AtmosphericBackdrop
              src={src}
              optimizedBase={optimizedBase}
              lqip={lqip}
              fallbackOrigin={origin === "center" ? "center" : origin}
              className={cn(
                "absolute select-none",
                originToPosition[origin],
                origin === "center"
                  ? "w-full h-full object-cover"
                  : "w-[60%] max-w-[820px] h-auto",
                tone === "dark" ? "mix-blend-screen" : "mix-blend-multiply dark:mix-blend-screen",
              )}
              noFade
            />
          </div>
        )}
        {glowSideClass(glow)}
        <div className={cn("relative", innerClassName)}>{children}</div>
      </section>
    );
  },
);

SectionAtmosphere.displayName = "SectionAtmosphere";

export default SectionAtmosphere;
